import { Cascader } from 'antd-mobile';
import React, { useEffect, useState } from 'react';
import { RightOutline } from 'antd-mobile-icons';
import s from '../index.module.scss';
import { fetchCascaderData, getCacheKey, buildTree, getMaxDepth } from './utils';
import ToastUtils from '@/utils/ToastUtils';
/**
 * BaseCascader - 通用级联基类组件
 *
 * 职责：取数 → 缓存 → 树转换 → 渲染 Cascader → 表单赋值
 *
 * Props:
 * - setting: 字段配置，含 data_source, data_source_address/requestUrl
 * - updated: 是否禁用编辑（0=可编辑）
 * - formInstance: Form 实例
 * - field_type: 字段类型
 * - en_name: 字段英文名
 * - customConvertToTree: (子类注入) 自定义扁平→树转换函数
 * - customHandleChange: (子类注入) 自定义 change 处理函数
 */
const BaseCascader = (props) => {
    const [casOptions, setOptions] = useState([]);
    const { setting = {}, updated, desc, formInstance, en_name } = props;
    const disabled = updated !== 0;
    const [visible, setVisible] = useState(false);
    const [value, setValue] = useState([]);
    const [maxDeep, setMaxDeep] = useState(0);

    /**
     * 初始化：获取数据并转换为树形结构
     */
    const handleInitial = async () => {
        const address = setting.data_source_address || setting.requestUrl;
        if (!address) return;

        const dataSource = setting.data_source || 'default';
        const cacheKey = getCacheKey(dataSource);

        // 1. 带缓存的取数
        const rawData = await fetchCascaderData(address, cacheKey);
        if (!rawData || rawData.length === 0) return;

        // 2. 树转换（可被子类覆盖）
        const convertFn = props.customConvertToTree || ((data) => buildTree(data));
        const treeOptions = convertFn(rawData, setting);

        setOptions(treeOptions);
        // 计算最大层级深度（用于自动校验）
        setMaxDeep(getMaxDepth(treeOptions));
    };

    useEffect(() => {
        handleInitial();
    }, []);

    // 同步外部传入的 value
    useEffect(() => {
        setValue(props.value || []);
    }, [props.value]);

    // 当 options 加载完成后，更新 name 字段
    /**
     * 根据选中的 codes 获取对应的 labels
     */
    function getLabelsByCodes(codes) {
        let currentLevel = casOptions;
        const labels = [];

        for (const code of codes) {
            const found = currentLevel.find((item) => item.value === code);
            if (!found) break;

            labels.push(found.label);
            if (found.children) {
                currentLevel = found.children;
            } else {
                break;
            }
        }

        return labels;
    }

    useEffect(() => {
        if (casOptions.length > 0) {
            const labels = getLabelsByCodes(props.value || []);
            formInstance?.setFieldsValue({
                [`${en_name}_name`]: labels
            });
        }
    }, [casOptions]);

    /**
     * 打开选择器
     */
    const selectItem = () => {
        if (!disabled) {
            setVisible(true);
            if (props?.handleDynamicFormItemFocusAndBlur) {
                props.handleDynamicFormItemFocusAndBlur(en_name);
            }
        }
        return false;
    };

    /**
     * 默认的确认处理（可被子类覆盖）
     */
    const defaultHandleConfirm = (val) => {
        setValue(val);
        const labels = getLabelsByCodes(val);
        formInstance?.setFieldsValue({
            [en_name]: val,
            [`${en_name}_name`]: labels
        });
        return true;
    };

    /**
     * 点击确定按钮
     */
    const onConfirmHandler = (val) => {
        if (props.customHandleChange) {
            // 使用子类注入的自定义处理逻辑（如 AreaCascader 的必选校验）
            const result = props.customHandleChange(val, { formInstance, en_name, getLabelsByCodes });
            if (result === false) {
                return false; // 子类阻止了选择
            }
        } else if (maxDeep > 0 && val?.length < maxDeep) {
            // 默认逻辑：未选够层级，阻止并提示
            ToastUtils.showToast('请选择');
            return false;
        } else {
            defaultHandleConfirm(val);
        }
    };

    return (
        <>
            <Cascader
                className={s['dynamic-cascade']}
                popupClassName={s['dynamic-cascade-popup']}
                options={casOptions}
                visible={visible}
                placeholder=""
                onClose={() => {
                    setVisible(false);
                    if (props?.handleDynamicFormItemFocusAndBlur) {
                        props.handleDynamicFormItemFocusAndBlur();
                    }
                }}
                value={value}
                onConfirm={(val) => onConfirmHandler(val)}
                style={{
                    width: '100%'
                }}
            >
                {(items) => {
                    if (items.every((item) => item === null)) {
                        return (
                            <div className={s['dynamic-cascade-item']} onClick={selectItem}>
                                {desc || '请选择'} <RightOutline fontSize={12} color="#999999" style={{ marginLeft: 8 }} />
                            </div>
                        );
                    }
                    return (
                        <div className={s['dynamic-cascade-item-value-wrap']} onClick={selectItem}>
                            <div className={s['dynamic-cascade-item-value']}>{items.map((item) => item?.label || desc || '请选择').join('/')}</div>
                            <RightOutline fontSize={12} color="#999999" style={{ marginLeft: 8 }} />
                        </div>
                    );
                }}
            </Cascader>
        </>
    );
};

export default BaseCascader;
